import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext.jsx';
import AppBarComponent from '../components/AppBarComponent';
import { apiUrl } from '../apiConfig';
import { motion, AnimatePresence } from 'framer-motion';
import 'bootstrap/dist/css/bootstrap.min.css';

const BRAND = '#facc15';
const BRAND_RGB = '250,204,21';

const fmt = (v) => v ?? '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';
const fmtMoney = (v) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const EMPTY_USER = { username: '', password: '', perfil: 'user', nome_completo: '', telefone: '', empresa: '' };
const EMPTY_PLAN = { nome: '', descricao: '', periodicidade: 'mensal', max_terminais: 1, preco: '', ativo: true };

const Badge = ({ children, color = BRAND }) => (
  <span style={{ display:'inline-block', background:`rgba(${color === BRAND ? BRAND_RGB : '59,130,246'},0.1)`, border:`1px solid rgba(${color === BRAND ? BRAND_RGB : '59,130,246'},0.3)`, borderRadius:100, padding:'3px 12px', fontSize:'0.7rem', fontWeight:700, color, letterSpacing:'0.06em', textTransform:'uppercase' }}>
    {children}
  </span>
);

const Toggle = ({ value, onChange, disabled }) => (
  <button type="button" onClick={() => !disabled && onChange(!value)} disabled={disabled}
    style={{ width:44, height:24, borderRadius:12, border:'none', cursor: disabled ? 'not-allowed' : 'pointer', background: value ? '#10b981' : 'rgba(255,255,255,0.15)', position:'relative', transition:'background 0.2s', flexShrink:0, padding:0 }}>
    <span style={{ position:'absolute', top:3, left: value ? 23 : 3, width:18, height:18, borderRadius:'50%', background:'#fff', transition:'left 0.2s', display:'block' }}/>
  </button>
);

const Input = ({ label, ...props }) => (
  <div>
    {label && <label style={{ display:'block', fontSize:'0.72rem', fontWeight:700, color:'rgba(241,245,249,0.45)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</label>}
    <input {...props} style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:10, padding:'10px 13px', color:'#f1f5f9', fontSize:'0.88rem', outline:'none', fontFamily:'inherit', ...(props.style || {}) }}/>
  </div>
);

const Select = ({ label, children, ...props }) => (
  <div>
    {label && <label style={{ display:'block', fontSize:'0.72rem', fontWeight:700, color:'rgba(241,245,249,0.45)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</label>}
    <select {...props} style={{ width:'100%', background:'#0f172a', border:'1px solid rgba(255,255,255,0.1)', borderRadius:10, padding:'10px 13px', color:'#f1f5f9', fontSize:'0.88rem', outline:'none', fontFamily:'inherit' }}>
      {children}
    </select>
  </div>
);

export default function UserAdmin() {
  const { user, getAuthHeaders } = useAuth();
  const [aba, setAba] = useState('ativacoes');

  // ===== USUÁRIOS =====
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userForm, setUserForm] = useState(EMPTY_USER);
  const [editingUserId, setEditingUserId] = useState(null);
  const [userSaving, setUserSaving] = useState(false);
  const [userError, setUserError] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);

  // ===== PLANOS =====
  const [planos, setPlanos] = useState([]);
  const [loadingPlanos, setLoadingPlanos] = useState(true);
  const [planForm, setPlanForm] = useState(EMPTY_PLAN);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [planSaving, setPlanSaving] = useState(false);
  const [planError, setPlanError] = useState(null);

  // ===== ATIVAÇÕES =====
  const [acessoSaving, setAcessoSaving] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const r = await fetch(apiUrl('/api/users'), { headers: getAuthHeaders() });
      const data = await r.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch { setUserError('Erro ao carregar usuários'); }
    finally { setLoadingUsers(false); }
  }, [getAuthHeaders]);

  const fetchPlanos = useCallback(async () => {
    setLoadingPlanos(true);
    try {
      const r = await fetch(apiUrl('/api/planos'), { headers: getAuthHeaders() });
      const data = await r.json();
      setPlanos(Array.isArray(data) ? data : []);
    } catch {}
    finally { setLoadingPlanos(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchUsers(); fetchPlanos(); }, [fetchUsers, fetchPlanos]);

  if (!user || user.perfil !== 'admin') {
    return (
      <div style={{ minHeight:'100vh', background:'#020617', color:'#f1f5f9', fontFamily:'Inter, system-ui, sans-serif' }}>
        <AppBarComponent/>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:'3rem', marginBottom:12 }}>🔒</div>
            <div style={{ fontWeight:700, fontSize:'1.1rem' }}>Acesso restrito a administradores</div>
          </div>
        </div>
      </div>
    );
  }

  // ===== HANDLERS USUÁRIOS =====
  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setUserSaving(true); setUserError(null);
    try {
      const method = editingUserId ? 'PUT' : 'POST';
      const url = editingUserId ? apiUrl(`/api/users/${editingUserId}`) : apiUrl('/api/users');
      const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify(userForm) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Erro ao salvar');
      await fetchUsers();
      setEditingUserId(null); setUserForm(EMPTY_USER);
    } catch (err) { setUserError(err.message); }
    finally { setUserSaving(false); }
  };

  const handleUserEdit = (u) => {
    setEditingUserId(u.id);
    setUserForm({ username: u.username, password: '', perfil: u.perfil, nome_completo: u.nome_completo || '', telefone: u.telefone || '', empresa: u.empresa || '' });
    setUserError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUserDelete = async (u) => {
    setDeletingUserId(u.id);
    try {
      await fetch(apiUrl(`/api/users/${u.id}`), { method: 'DELETE', headers: getAuthHeaders() });
      setUsers(prev => prev.filter(x => x.id !== u.id));
      setConfirmDeleteUser(null);
    } catch { setUserError('Erro ao apagar usuário'); }
    finally { setDeletingUserId(null); }
  };

  // ===== HANDLERS PLANOS =====
  const handlePlanSubmit = async (e) => {
    e.preventDefault();
    setPlanSaving(true); setPlanError(null);
    try {
      const method = editingPlanId ? 'PUT' : 'POST';
      const url = editingPlanId ? apiUrl(`/api/planos/${editingPlanId}`) : apiUrl('/api/planos');
      const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ ...planForm, preco: parseFloat(planForm.preco) || 0 }) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Erro ao salvar plano');
      await fetchPlanos();
      setEditingPlanId(null); setPlanForm(EMPTY_PLAN);
    } catch (err) { setPlanError(err.message); }
    finally { setPlanSaving(false); }
  };

  const handlePlanEdit = (p) => {
    setEditingPlanId(p.id);
    setPlanForm({ nome: p.nome, descricao: p.descricao || '', periodicidade: p.periodicidade, max_terminais: p.max_terminais, preco: String(p.preco), ativo: p.ativo });
    setPlanError(null);
  };

  const handlePlanDelete = async (id) => {
    if (!window.confirm('Apagar este plano?')) return;
    try {
      await fetch(apiUrl(`/api/planos/${id}`), { method: 'DELETE', headers: getAuthHeaders() });
      setPlanos(prev => prev.filter(p => p.id !== id));
    } catch { setPlanError('Erro ao apagar plano'); }
  };

  // ===== HANDLERS ATIVAÇÕES =====
  const handleToggleAcesso = async (userId, field, value) => {
    setAcessoSaving(`${userId}_${field}`);
    try {
      const r = await fetch(apiUrl(`/api/users/${userId}/acesso`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ [field]: value }),
      });
      if (r.ok) setUsers(prev => prev.map(u => u.id === userId ? { ...u, [field]: value } : u));
    } catch {}
    finally { setAcessoSaving(null); }
  };

  const handleAssignPlan = async (userId, plano_id) => {
    setAcessoSaving(`${userId}_plano`);
    const plano = planos.find(p => p.id === parseInt(plano_id));
    let plano_inicio = null, plano_expira = null;
    if (plano) {
      const inicio = new Date();
      const expira = new Date(inicio);
      if (plano.periodicidade === 'anual') expira.setFullYear(expira.getFullYear() + 1);
      else expira.setMonth(expira.getMonth() + 1);
      plano_inicio = inicio.toISOString();
      plano_expira = expira.toISOString();
    }
    try {
      const r = await fetch(apiUrl(`/api/users/${userId}/acesso`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ plano_id: plano_id ? parseInt(plano_id) : null, plano_inicio, plano_expira }),
      });
      if (r.ok) setUsers(prev => prev.map(u => u.id === userId ? { ...u, plano_id: plano_id ? parseInt(plano_id) : null, plano_nome: plano?.nome || null, plano_inicio, plano_expira } : u));
    } catch {}
    finally { setAcessoSaving(null); }
  };

  const cardStyle = { background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:20, padding:'28px 24px' };
  const btnStyle = { background:`rgba(${BRAND_RGB},0.1)`, border:`1px solid rgba(${BRAND_RGB},0.3)`, borderRadius:10, padding:'10px 20px', color:BRAND, fontWeight:700, fontSize:'0.85rem', cursor:'pointer', transition:'all 0.2s' };
  const btnDanger = { background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:10, padding:'8px 16px', color:'#fca5a5', fontWeight:600, fontSize:'0.82rem', cursor:'pointer', transition:'all 0.2s' };
  const btnSecondary = { background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:10, padding:'8px 16px', color:'rgba(241,245,249,0.6)', fontWeight:600, fontSize:'0.82rem', cursor:'pointer', transition:'all 0.2s' };

  return (
    <div style={{ minHeight:'100vh', background:'#020617', color:'#f1f5f9', fontFamily:'Inter, system-ui, sans-serif' }}>
      <style>{`
        * { box-sizing: border-box; }
        .adm-input:focus { border-color: rgba(${BRAND_RGB},0.5) !important; }
        .adm-row:hover { background: rgba(255,255,255,0.025); }
        @keyframes spin { to { transform: rotate(360deg); } }
        .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.7); display:flex; align-items:center; justify-content:center; z-index:1000; padding:24px; }
      `}</style>

      <AppBarComponent/>

      <div style={{ maxWidth:1200, margin:'0 auto', padding:'40px 24px' }}>
        <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.4 }}>

          {/* Header */}
          <div style={{ marginBottom:32 }}>
            <span style={{ display:'inline-block', background:`rgba(${BRAND_RGB},0.08)`, border:`1px solid rgba(${BRAND_RGB},0.25)`, borderRadius:100, padding:'4px 14px', fontSize:'0.7rem', fontWeight:700, color:BRAND, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:10 }}>Admin</span>
            <h1 style={{ fontSize:'1.7rem', fontWeight:900, margin:0 }}>Painel de Administração</h1>
          </div>

          {/* Abas */}
          <div style={{ display:'flex', gap:4, marginBottom:32, borderBottom:'1px solid rgba(255,255,255,0.07)', paddingBottom:0 }}>
            {[['ativacoes','⚡ Acessos & Ativações'],['usuarios','👥 Cadastros'],['planos','📋 Planos']].map(([key,label])=>(
              <button key={key} onClick={()=>setAba(key)}
                style={{ background: aba===key ? `rgba(${BRAND_RGB},0.1)` : 'transparent', border:'none', borderBottom: aba===key ? `2px solid ${BRAND}` : '2px solid transparent', borderRadius:0, padding:'10px 22px', color: aba===key ? BRAND : 'rgba(241,245,249,0.5)', fontWeight:700, fontSize:'0.88rem', cursor:'pointer', transition:'all 0.2s', marginBottom:-1 }}>
                {label}
              </button>
            ))}
          </div>

          {/* ===== ABA USUÁRIOS ===== */}
          {aba === 'usuarios' && (
            <div>
              {/* Formulário */}
              <div style={{ ...cardStyle, marginBottom:24 }}>
                <h3 style={{ fontWeight:800, fontSize:'1rem', marginBottom:20, color:BRAND }}>{editingUserId ? '✏️ Editar usuário' : '➕ Novo usuário'}</h3>
                {userError && <div style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:10, padding:'10px 14px', color:'#fca5a5', fontSize:'0.85rem', marginBottom:16 }}>⚠ {userError}</div>}
                <form onSubmit={handleUserSubmit}>
                  <div className="row g-3">
                    <div className="col-md-4"><Input label="Usuário *" name="username" value={userForm.username} onChange={e=>setUserForm({...userForm, username:e.target.value})} required/></div>
                    <div className="col-md-4"><Input label={editingUserId ? 'Nova senha (deixe em branco para manter)' : 'Senha *'} type="password" name="password" value={userForm.password} onChange={e=>setUserForm({...userForm, password:e.target.value})} required={!editingUserId}/></div>
                    <div className="col-md-4"><Input label="Nome completo *" name="nome_completo" value={userForm.nome_completo} onChange={e=>setUserForm({...userForm, nome_completo:e.target.value})} required/></div>
                    <div className="col-md-4"><Input label="Telefone" name="telefone" value={userForm.telefone} onChange={e=>setUserForm({...userForm, telefone:e.target.value})}/></div>
                    <div className="col-md-5"><Input label="Empresa" name="empresa" value={userForm.empresa} onChange={e=>setUserForm({...userForm, empresa:e.target.value})}/></div>
                    <div className="col-md-3">
                      <Select label="Perfil" value={userForm.perfil} onChange={e=>setUserForm({...userForm, perfil:e.target.value})}>
                        <option value="user">Usuário</option>
                        <option value="admin">Admin</option>
                      </Select>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:10, marginTop:20 }}>
                    <button type="submit" style={btnStyle} disabled={userSaving}>
                      {userSaving ? '...' : (editingUserId ? 'Salvar alterações' : 'Criar usuário')}
                    </button>
                    {editingUserId && (
                      <button type="button" style={btnSecondary} onClick={()=>{ setEditingUserId(null); setUserForm(EMPTY_USER); setUserError(null); }}>
                        Cancelar
                      </button>
                    )}
                  </div>
                </form>
              </div>

              {/* Tabela */}
              <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
                <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'grid', gridTemplateColumns:'1fr 130px 120px 100px 120px', gap:12, fontSize:'0.7rem', fontWeight:700, color:'rgba(241,245,249,0.3)', textTransform:'uppercase', letterSpacing:'0.07em' }}>
                  <span>Usuário</span><span>Empresa</span><span>Perfil</span><span>Ativo</span><span style={{textAlign:'center'}}>Ações</span>
                </div>
                {loadingUsers ? (
                  <div style={{ padding:'40px', textAlign:'center', color:'rgba(241,245,249,0.4)' }}>Carregando...</div>
                ) : users.map((u, i) => (
                  <div key={u.id} className="adm-row" style={{ padding:'14px 20px', borderBottom: i < users.length-1 ? '1px solid rgba(255,255,255,0.04)' : 'none', display:'grid', gridTemplateColumns:'1fr 130px 120px 100px 120px', gap:12, alignItems:'center' }}>
                    <div>
                      <div style={{ fontWeight:600, fontSize:'0.88rem' }}>{u.nome_completo}</div>
                      <div style={{ fontSize:'0.72rem', color:'rgba(241,245,249,0.35)', marginTop:2 }}>{u.username}</div>
                    </div>
                    <div style={{ fontSize:'0.82rem', color:'rgba(241,245,249,0.5)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{fmt(u.empresa)}</div>
                    <div><Badge color={u.perfil==='admin'?BRAND:'#60a5fa'}>{u.perfil}</Badge></div>
                    <div><Badge color={u.ativo?'#10b981':'#ef4444'}>{u.ativo?'Ativo':'Inativo'}</Badge></div>
                    <div style={{ display:'flex', gap:6, justifyContent:'center' }}>
                      <button style={{ ...btnSecondary, padding:'6px 12px', fontSize:'0.78rem' }} onClick={()=>handleUserEdit(u)}>✏️</button>
                      <button style={{ ...btnDanger, padding:'6px 12px', fontSize:'0.78rem' }} onClick={()=>setConfirmDeleteUser(u)}>🗑</button>
                    </div>
                  </div>
                ))}
                <div style={{ padding:'10px 20px', borderTop:'1px solid rgba(255,255,255,0.04)', fontSize:'0.75rem', color:'rgba(241,245,249,0.25)', textAlign:'right' }}>{users.length} usuário{users.length!==1?'s':''}</div>
              </div>
            </div>
          )}

          {/* ===== ABA PLANOS ===== */}
          {aba === 'planos' && (
            <div>
              <div style={{ ...cardStyle, marginBottom:24 }}>
                <h3 style={{ fontWeight:800, fontSize:'1rem', marginBottom:20, color:BRAND }}>{editingPlanId ? '✏️ Editar plano' : '➕ Novo plano'}</h3>
                {planError && <div style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:10, padding:'10px 14px', color:'#fca5a5', fontSize:'0.85rem', marginBottom:16 }}>⚠ {planError}</div>}
                <form onSubmit={handlePlanSubmit}>
                  <div className="row g-3">
                    <div className="col-md-5"><Input label="Nome do plano *" value={planForm.nome} onChange={e=>setPlanForm({...planForm,nome:e.target.value})} required/></div>
                    <div className="col-md-3">
                      <Select label="Periodicidade" value={planForm.periodicidade} onChange={e=>setPlanForm({...planForm,periodicidade:e.target.value})}>
                        <option value="mensal">Mensal</option>
                        <option value="anual">Anual</option>
                      </Select>
                    </div>
                    <div className="col-md-2"><Input label="Terminais" type="number" min={1} value={planForm.max_terminais} onChange={e=>setPlanForm({...planForm,max_terminais:parseInt(e.target.value)||1})}/></div>
                    <div className="col-md-2"><Input label="Preço (R$)" type="number" min={0} step="0.01" value={planForm.preco} onChange={e=>setPlanForm({...planForm,preco:e.target.value})}/></div>
                    <div className="col-12"><Input label="Descrição" value={planForm.descricao} onChange={e=>setPlanForm({...planForm,descricao:e.target.value})}/></div>
                    <div className="col-12" style={{ display:'flex', alignItems:'center', gap:12 }}>
                      <Toggle value={planForm.ativo} onChange={v=>setPlanForm({...planForm,ativo:v})}/>
                      <span style={{ fontSize:'0.85rem', color:'rgba(241,245,249,0.6)' }}>Plano ativo (visível)</span>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:10, marginTop:20 }}>
                    <button type="submit" style={btnStyle} disabled={planSaving}>{planSaving ? '...' : (editingPlanId ? 'Salvar' : 'Criar plano')}</button>
                    {editingPlanId && <button type="button" style={btnSecondary} onClick={()=>{ setEditingPlanId(null); setPlanForm(EMPTY_PLAN); }}>Cancelar</button>}
                  </div>
                </form>
              </div>

              <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
                <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'grid', gridTemplateColumns:'1fr 110px 100px 110px 80px 120px', gap:12, fontSize:'0.7rem', fontWeight:700, color:'rgba(241,245,249,0.3)', textTransform:'uppercase', letterSpacing:'0.07em' }}>
                  <span>Plano</span><span>Periodicidade</span><span>Terminais</span><span>Preço</span><span>Status</span><span style={{textAlign:'center'}}>Ações</span>
                </div>
                {loadingPlanos ? (
                  <div style={{ padding:'40px', textAlign:'center', color:'rgba(241,245,249,0.4)' }}>Carregando...</div>
                ) : planos.length === 0 ? (
                  <div style={{ padding:'40px', textAlign:'center', color:'rgba(241,245,249,0.3)' }}>Nenhum plano cadastrado</div>
                ) : planos.map((p, i) => (
                  <div key={p.id} className="adm-row" style={{ padding:'14px 20px', borderBottom: i < planos.length-1 ? '1px solid rgba(255,255,255,0.04)':'none', display:'grid', gridTemplateColumns:'1fr 110px 100px 110px 80px 120px', gap:12, alignItems:'center' }}>
                    <div>
                      <div style={{ fontWeight:600, fontSize:'0.88rem' }}>{p.nome}</div>
                      {p.descricao && <div style={{ fontSize:'0.72rem', color:'rgba(241,245,249,0.35)', marginTop:2 }}>{p.descricao}</div>}
                    </div>
                    <div><Badge color="#8b5cf6">{p.periodicidade}</Badge></div>
                    <div style={{ fontSize:'0.85rem', color:'rgba(241,245,249,0.6)' }}>{p.max_terminais >= 999 ? 'Ilimitado' : p.max_terminais}</div>
                    <div style={{ fontWeight:700, color:BRAND, fontSize:'0.9rem' }}>{fmtMoney(p.preco)}</div>
                    <div><Badge color={p.ativo?'#10b981':'#ef4444'}>{p.ativo?'Ativo':'Off'}</Badge></div>
                    <div style={{ display:'flex', gap:6, justifyContent:'center' }}>
                      <button style={{ ...btnSecondary, padding:'6px 12px', fontSize:'0.78rem' }} onClick={()=>handlePlanEdit(p)}>✏️</button>
                      <button style={{ ...btnDanger, padding:'6px 12px', fontSize:'0.78rem' }} onClick={()=>handlePlanDelete(p.id)}>🗑</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ===== ABA ATIVAÇÕES ===== */}
          {aba === 'ativacoes' && (
            <div>
              <div style={{ background:`rgba(${BRAND_RGB},0.05)`, border:`1px solid rgba(${BRAND_RGB},0.2)`, borderRadius:14, padding:'14px 20px', marginBottom:24, fontSize:'0.85rem', color:'rgba(241,245,249,0.6)', lineHeight:1.6 }}>
                ⚡ <strong style={{ color:BRAND }}>Acessos & Ativações:</strong> Ative/desative contas e libere o <strong style={{color:'#a78bfa'}}>Modo ADM (laudo completo)</strong> por usuário. Sem essa opção ativa, o usuário vê apenas o valor total de divergência.
              </div>
              <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
                <div style={{ padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'grid', gridTemplateColumns:'1.4fr 120px 160px 190px 160px', gap:12, fontSize:'0.7rem', fontWeight:700, color:'rgba(241,245,249,0.3)', textTransform:'uppercase', letterSpacing:'0.07em' }}>
                  <span>Usuário</span>
                  <span style={{textAlign:'center'}}>Conta ativa</span>
                  <span style={{textAlign:'center'}}>Modo ADM<br/><span style={{fontSize:'0.6rem',color:'rgba(167,139,250,0.6)'}}>Laudo completo</span></span>
                  <span>Plano</span>
                  <span>Expira em</span>
                </div>
                {loadingUsers ? (
                  <div style={{ padding:'40px', textAlign:'center', color:'rgba(241,245,249,0.4)' }}>Carregando...</div>
                ) : users.map((u, i) => (
                  <div key={u.id} className="adm-row" style={{ padding:'14px 20px', borderBottom: i < users.length-1 ? '1px solid rgba(255,255,255,0.04)':'none', display:'grid', gridTemplateColumns:'1.4fr 120px 160px 190px 160px', gap:12, alignItems:'center', opacity: u.perfil==='admin' ? 0.6 : 1 }}>
                    <div>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontWeight:600, fontSize:'0.88rem' }}>{u.nome_completo || u.username}</span>
                        {u.perfil === 'admin' && <Badge color={BRAND}>admin</Badge>}
                      </div>
                      <div style={{ fontSize:'0.72rem', color:'rgba(241,245,249,0.35)', marginTop:1 }}>{u.username}</div>
                      {u.empresa && <div style={{ fontSize:'0.7rem', color:'rgba(241,245,249,0.25)', marginTop:1 }}>{u.empresa}</div>}
                    </div>

                    <div style={{ display:'flex', justifyContent:'center' }}>
                      <Toggle value={!!u.ativo} onChange={v=>handleToggleAcesso(u.id,'ativo',v)} disabled={u.perfil==='admin' || acessoSaving===`${u.id}_ativo`}/>
                    </div>

                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                      <Toggle value={!!u.acesso_completo} onChange={v=>handleToggleAcesso(u.id,'acesso_completo',v)} disabled={u.perfil==='admin' || acessoSaving===`${u.id}_acesso_completo`}/>
                      <span style={{ fontSize:'0.65rem', color: u.acesso_completo ? '#a78bfa' : 'rgba(241,245,249,0.25)', fontWeight:600 }}>
                        {u.perfil==='admin' ? 'sempre ativo' : u.acesso_completo ? 'liberado' : 'bloqueado'}
                      </span>
                    </div>

                    <div>
                      {u.perfil === 'admin' ? (
                        <span style={{ fontSize:'0.78rem', color:'rgba(241,245,249,0.25)', fontStyle:'italic' }}>N/A</span>
                      ) : (
                        <select
                          value={u.plano_id || ''}
                          onChange={e=>handleAssignPlan(u.id, e.target.value || null)}
                          disabled={acessoSaving===`${u.id}_plano`}
                          style={{ background:'#0f172a', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'7px 10px', color:'#f1f5f9', fontSize:'0.82rem', width:'100%', outline:'none' }}>
                          <option value="">— Sem plano —</option>
                          {planos.filter(p=>p.ativo).map(p=>(
                            <option key={p.id} value={p.id}>{p.nome} ({p.periodicidade})</option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div style={{ fontSize:'0.82rem', color: u.plano_expira && new Date(u.plano_expira) < new Date() ? '#ef4444' : 'rgba(241,245,249,0.5)' }}>
                      {u.perfil === 'admin' ? '—' : u.plano_expira ? (
                        <>
                          {fmtDate(u.plano_expira)}
                          {new Date(u.plano_expira) < new Date() && <span style={{ display:'block', fontSize:'0.68rem', color:'#ef4444', fontWeight:700 }}>EXPIRADO</span>}
                        </>
                      ) : '—'}
                    </div>
                  </div>
                ))}
                <div style={{ padding:'10px 20px', borderTop:'1px solid rgba(255,255,255,0.04)', fontSize:'0.75rem', color:'rgba(241,245,249,0.25)', textAlign:'right' }}>{users.length} usuário{users.length!==1?'s':''}</div>
              </div>
            </div>
          )}

        </motion.div>
      </div>

      {/* Modal confirmar exclusão */}
      <AnimatePresence>
        {confirmDeleteUser && (
          <div className="modal-overlay" onClick={()=>setConfirmDeleteUser(null)}>
            <motion.div initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}} exit={{opacity:0,scale:0.95}} transition={{duration:0.2}}
              onClick={e=>e.stopPropagation()}
              style={{ background:'#0f172a', border:'1px solid rgba(255,255,255,0.1)', borderRadius:18, padding:28, maxWidth:420, width:'100%' }}>
              <div style={{ fontSize:'1.5rem', marginBottom:10 }}>🗑</div>
              <h3 style={{ fontWeight:800, fontSize:'1.05rem', marginBottom:8 }}>Confirmar exclusão</h3>
              <p style={{ color:'rgba(241,245,249,0.55)', fontSize:'0.88rem', marginBottom:6 }}>Excluir o usuário:</p>
              <p style={{ color:BRAND, fontWeight:700, marginBottom:20 }}>"{confirmDeleteUser.nome_completo}" ({confirmDeleteUser.username})</p>
              <p style={{ fontSize:'0.78rem', color:'rgba(241,245,249,0.3)', marginBottom:24 }}>Todos os arquivos e análises serão apagados permanentemente.</p>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                <button onClick={()=>setConfirmDeleteUser(null)} style={btnSecondary} disabled={!!deletingUserId}>Cancelar</button>
                <button onClick={()=>handleUserDelete(confirmDeleteUser)} style={btnDanger} disabled={!!deletingUserId}>
                  {deletingUserId ? '...' : 'Excluir'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
